import { useRef, useState } from 'react';

import { IconThumbDown, IconThumbUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  buildSmartSearchXpFeedbackRequestBody,
  SMART_SEARCH_FEEDBACK_COMMENT_MAX_LENGTH,
  smartSearchThumbsToFeedbackValue,
  type SmartSearchUserFeedbackValue,
} from '@cfa/pathway-fe-shared/smart-search-feedback';
import {
  SmartAnswersResult,
  upsertSmartSearchFeedback,
} from '@cfa/pathway-fe-shared/xp';
import { Typography } from '@cfa/react-components';
import { TextInput } from '@cfa/react-core';

import PathwayModal from '@/components/popups/PathwayModal';

import { ThankYou } from '@/icons';

interface FeedbackProps {
  smartAnswer?: SmartAnswersResult;
  question: string;
}

const Feedback: React.FC<FeedbackProps> = ({ smartAnswer }) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [comment, setComment] = useState('');
  const [pendingValue, setPendingValue] =
    useState<SmartSearchUserFeedbackValue | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const feedbackSessionRef = useRef(0);

  if (!smartAnswer?.traceId) {
    return null;
  }

  const traceId = smartAnswer.traceId;

  const bumpFeedbackSession = () => {
    feedbackSessionRef.current += 1;
  };

  const handleThumbClick = (thumbsUp: boolean) => {
    bumpFeedbackSession();
    setIsSubmitting(false);
    const value = smartSearchThumbsToFeedbackValue(thumbsUp);
    setPendingValue(value);
    setComment('');
    setSubmitError(null);
    setValidationError(null);
    setIsSubmitted(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    bumpFeedbackSession();
    setIsOpen(false);
    setComment('');
    setPendingValue(null);
    setSubmitError(null);
    setValidationError(null);
    setIsSubmitted(false);
    setIsSubmitting(false);
  };

  const handleSubmitComment = async () => {
    if (pendingValue == null) {
      return;
    }

    const trimmed = comment.trim();
    if (!trimmed) {
      setValidationError(t('AISearchPOC.feedbackCommentRequired'));
      return;
    }

    const session = feedbackSessionRef.current;
    setValidationError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await upsertSmartSearchFeedback({
        path: { traceId },
        body: buildSmartSearchXpFeedbackRequestBody({
          value: pendingValue,
          comment: trimmed,
        }),
        throwOnError: true,
      });
      if (feedbackSessionRef.current !== session) {
        return;
      }
      setIsSubmitted(true);
    } catch {
      if (feedbackSessionRef.current !== session) {
        return;
      }
      setSubmitError(t('AISearchPOC.feedbackCommentError'));
    } finally {
      if (feedbackSessionRef.current === session) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      <div className="flex gap-4 pr-5">
        <button
          type="button"
          aria-label={t('AISearchPOC.thumbsUpFeedback')}
          onClick={() => handleThumbClick(true)}
        >
          <IconThumbUp
            aria-hidden
            className="text-brand-navy dark:text-contrast cursor-pointer transition-all hover:scale-105 active:scale-90"
          />
        </button>
        <button
          type="button"
          aria-label={t('AISearchPOC.thumbsDownFeedback')}
          onClick={() => handleThumbClick(false)}
        >
          <IconThumbDown
            aria-hidden
            className="text-brand-navy dark:text-contrast cursor-pointer transition-all hover:scale-105 active:scale-90"
          />
        </button>
      </div>
      <PathwayModal
        isOpen={isOpen}
        onClose={handleClose}
        stepKey={
          isSubmitted
            ? 'smart-search-feedback-thanks'
            : 'smart-search-feedback-form'
        }
        size="sm"
        className="max-w-[480px]!"
      >
        {!isSubmitted ? (
          <>
            <PathwayModal.Header>
              <PathwayModal.Title>
                {t('AISearchPOC.feedbackCommentTitle')}
              </PathwayModal.Title>
            </PathwayModal.Header>
            <PathwayModal.Body noMinHeight>
              <div data-testid="SmartSearchFeedbackModal">
                <p className="text-variant-body1 text-grayscale-gray6 dark:text-contrast mb-6">
                  {t('AISearchPOC.feedbackCommentDescription')}
                </p>
                {submitError ? (
                  <Typography
                    variant="body2"
                    className="text-error! mb-4"
                    role="alert"
                  >
                    {submitError}
                  </Typography>
                ) : null}
                <TextInput
                  aria-label={t('AISearchPOC.feedbackCommentTitle')}
                  className="w-full [&_label]:hidden [&_textarea]:min-h-28 [&_textarea]:resize-none"
                  errorMessage={validationError ?? ''}
                  isInvalid={validationError != null}
                  maxLength={SMART_SEARCH_FEEDBACK_COMMENT_MAX_LENGTH}
                  multiline
                  onChange={value => {
                    setComment(value);
                    if (validationError != null) {
                      setValidationError(null);
                    }
                    if (submitError != null) {
                      setSubmitError(null);
                    }
                  }}
                  placeholder={t('AISearchPOC.feedbackCommentPlaceholder')}
                  value={comment}
                />
              </div>
            </PathwayModal.Body>
            <PathwayModal.Footer>
              <PathwayModal.SecondaryButton onPress={handleClose}>
                {t('Button.cancel')}
              </PathwayModal.SecondaryButton>
              <PathwayModal.PrimaryButton
                isPending={isSubmitting}
                onPress={() => {
                  void handleSubmitComment();
                }}
                testId="SmartSearchFeedbackSubmit"
              >
                {t('AISearchPOC.feedbackCommentSubmit')}
              </PathwayModal.PrimaryButton>
            </PathwayModal.Footer>
          </>
        ) : (
          <>
            <PathwayModal.Body noMinHeight className="text-center">
              <div className="flex flex-col items-center justify-center px-2 py-8 sm:px-6">
                <ThankYou className="size-30" />
                <Typography
                  variant="h2"
                  className="mt-10! mb-8!"
                  fontWeight="medium"
                >
                  {t('Settings.thankYou')}
                </Typography>
                <Typography variant="body1" className="text-grayscale-gray4!">
                  {t('Settings.acknowledgement')}
                </Typography>
              </div>
            </PathwayModal.Body>
            <PathwayModal.Footer>
              <PathwayModal.PrimaryButton onPress={handleClose}>
                {t('Button.close')}
              </PathwayModal.PrimaryButton>
            </PathwayModal.Footer>
          </>
        )}
      </PathwayModal>
    </>
  );
};

export default Feedback;
